import Group from '@/types/Group.interface'
import Invoice from '@/types/Invoice.interface'
import { InvoiceParams, MediaParams } from '@/types/PaginationParams.interface'
import Media from '@/types/Media.interface'
import Pagination from '@/types/Pagination.interface'
import Person from '@/types/Person.interface'
import PersonProperty from '@/types/PersonProperty.interface'
import PhoneNumber from 'awesome-phonenumber'

interface State {
    //  Person media
    personSelectedMedia: Media[]        //  The current media belonging to the current selected person
    personSelectedMediaCurrentPage: number
    personSelectedMediaLastPage: number
    personSelectedMediaNextPage: number
    personSelectedMediaPaginating: boolean
    personSelectedMediaParams: MediaParams
    personSelectedMediaParamsDefault: MediaParams
    //  Person invoices
    personSelectedInvoices: Invoice[]   //  The current invoice belonging to the current selected person
    personSelectedInvoicesCurrentPage: number
    personSelectedInvoicesLastPage: number
    personSelectedInvoicesNextPage: number
    personSelectedInvoicesParams: InvoiceParams
    personSelectedInvoicesParamsDefault: InvoiceParams
    personSelectedInvoicesPaginating: boolean
    personProperties: PersonProperty[]  //  The person properties that define a perso
    personGroups: Group[]               //  The groups we keep track of in our person module
}

const defaults = () => {
    return{
        personGroups: [],
        personSelectedMedia: [],
        personSelectedMediaCurrentPage: null,
        personSelectedMediaLastPage: null,
        personSelectedMediaNextPage: null,
        personSelectedMediaPaginating: false,
        personSelectedMediaParams: { page: 1, filter: 'images' },
        personSelectedMediaParamsDefault: { page: 1, filter: 'images' },
        personSelectedInvoices: [],
        personSelectedInvoicesCurrentPage: null,
        personSelectedInvoicesLastPage: null,
        personSelectedInvoicesNextPage: null,
        personSelectedInvoicesParams: { page: 1, filter: 'open' },
        personSelectedInvoicesParamsDefault: { page: 1, filter: 'open' },
        personSelectedInvoicesPaginating: false,
        personProperties: null
    }
}

export default {
    state: defaults(),
    mutations: {
        personGroupsPush (state: State, groups: Group[]): void {
            //  Update our list
            state.personGroups = window.removeDuplicatesById(state.personGroups, groups)
        },
        personGroupsRemove(state: State, ids: number[]): void {
            //  Get our current lists of groups
            const groups = state.personGroups

            //  If no groups, do nothing
            if (!groups) return

            //  Else, filter out our removed ids
            const filtered = groups.filter((x: Group) => !ids.includes(x.id))

            //  Set our groups to the filtered list
            state.personGroups = filtered
        },
        personGroupsSet (state: State, groups: Group[]): void {
            state.personGroups = groups
        },
        personPropertiesSet (state: State, array: PersonProperty[]): void {
            state.personProperties = array
        },
        //  PERSON SELECTED MEDIA
        personSelectedMediaPaginationPush (state: State, page: Pagination): void {
            //  If page 1, then reset the media
            if (page.current_page == 1) state.personSelectedMedia = []

            //  Set our pages
            state.personSelectedMediaCurrentPage = page.current_page
            state.personSelectedMediaLastPage = page.last_page
            state.personSelectedMediaNextPage = page.current_page + 1
        },
        personSelectedMediaPaginationReset (state: State): void {
            //  Set our pages to null
            state.personSelectedMediaCurrentPage = null
            state.personSelectedMediaLastPage = null
            state.personSelectedMediaNextPage = null

            //  Reset our pagination params
            state.personSelectedMediaParams = state.personSelectedMediaParamsDefault
        },
        personSelectedMediaPaginatingSet (state: State, bool: boolean): void {
            state.personSelectedMediaPaginating = bool
        },
        personSelectedMediaParamsSet (state: State, params: MediaParams): void {
            state.personSelectedMediaParams = params
        },
        personSelectedMediaPush (state: State, media: Media[]): void {
            state.personSelectedMedia = window.removeDuplicatesById(state.personSelectedMedia, media)
        },
        personSelectedMediaRemove (state: State, ids: number[]): void {
            //  Get our current list of media
            const media = state.personSelectedMedia

            //  If no media, do nothing
            if (!media) return

            //  Else filter out the removed ids
            const filtered = media.filter((x: Media) => !ids.includes(x.id))

            //  Replace our media list with the filtered list
            state.personSelectedMedia = filtered
        },
        personSelectedMediaSet (state: State, media: Media[]): void {
            state.personSelectedMedia = media
        },
        //  PERSON SELECTED INVOICES
        personSelectedInvoicesParamsSet (state: State, params: InvoiceParams): void {
            state.personSelectedInvoicesParams = params
        },
        personSelectedInvoicesPush (state: State, invoices: Invoice[]): void {
            state.personSelectedInvoices = window.removeDuplicatesById(state.personSelectedInvoices, invoices)
        },
        personSelectedInvoicesRemove (state: State, ids: number[]): void {
            //  Get our current lists of invoices
            const invoices = state.personSelectedInvoices

            //  If no invoices, do nothing
            if (!invoices) return

            //  Else, filter out our removed ids
            const filtered = invoices.filter((x: Invoice) => !ids.includes(x.id))

            //  Replace with the filtered list
            state.personSelectedInvoices = filtered
        },
        personSelectedInvoicesSet (state: State, invoices: Invoice[]): void {
            state.personSelectedInvoices = invoices
        },
        personSelectedInvoicesSync (state: State, updates: Invoice[]): void {
            //  Get our list of invoices
            const invoices = state.personSelectedInvoices

            //  If no invoices, do nothing
            if (!invoices) return

            //  Grab our invoice filter params
            const params = state.personSelectedInvoicesParams

            //  Apply our inline filtering
            const filtered = updates.appSyncFilter(params, 'payments')

            //  Find the items we will have to remove because of the filter
            const remove = window._.differenceBy(updates, filtered, 'id')

            //  Update our list with the filtered array
            state.personSelectedInvoices = window.removeDuplicatesById(state.personSelectedInvoices, filtered, remove)
        },
        personSelectedInvoicesPaginationPush (state: State, page: Pagination): void {
            //  If this is page one, reset our invoices list
            if (page.current_page === 1) state.personSelectedInvoices = []

            state.personSelectedInvoicesCurrentPage = page.current_page
            state.personSelectedInvoicesLastPage = page.last_page
            state.personSelectedInvoicesNextPage = page.current_page + 1
        },
        personSelectedInvoicesPaginationReset (state: State): void {
            //  Set our pages to null
            state.personSelectedInvoicesCurrentPage = null
            state.personSelectedInvoicesLastPage = null
            state.personSelectedInvoicesNextPage = null

            //  Reset our pagination params
            state.personSelectedInvoicesParams = state.personSelectedInvoicesParamsDefault
        },
        personSelectedInvoicesPaginatingSet (state: State, bool: boolean): void {
            state.personSelectedInvoicesPaginating = bool
        },
        personReset (state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        personSelectedMediaPaginate ({ commit, getters }, personId: number) {
            //  Assemble our pagination params
            const p = getters.personSelectedMediaParams

            //  Attach the person id
            p.person_id = personId

            //  Start pagination tracker
            commit('personSelectedMediaPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/media?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our pagination params
                commit('personSelectedMediaPaginationPush', response.data)

                //  Set the person media
                commit('personSelectedMediaPush', response.data.data)

                //  Stop pagination tracker
                commit('personSelectedMediaPaginatingSet', false)

                //  Return the pagination data
                return response.data
            }).catch(() => null)
        },
        personSelectedInvoicesPaginate ({ commit, getters }, personId: number) {
            //  Assemble our pagination params
            const p = getters.personSelectedInvoicesParams

            //  Attach the person id
            p.person_id = personId

            //  Start our pagination tracker
            commit('personSelectedInvoicesPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/invoices?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our pagination params
                commit('personSelectedInvoicesPaginationPush', response.data)

                //  Update the payments invoices list
                commit('personSelectedInvoicesPush', response.data.data)

                //  Stop our pagination tracker
                commit('personSelectedInvoicesPaginatingSet', false)

                //  Return the pagination data
                return response.data
            }).catch(e => console.error('PersonSelectedInvoicesPaginate ERROR', e))
        },
        personPropertiesGet ({ commit, getters }): Promise<PersonProperty> {
            //  Assemble the API endpoint
            const url = `${getters.api}/person/properties`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our properties in the store
                commit('personPropertiesSet', response.data);

                //  Return our properties
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        hasMobile: () => (person: Person): boolean => {
            //  If the person does NOT exist, return false
            if (!person) return false

            //  Instantiate validator with person's default phone
            const num = window.getProps(person,['phone_default','msisdn']) || ''
            const phone = new PhoneNumber(num, 'US')

            //  Check if the phone is valid
            const valid = phone.isValid()

            //  Return valid bool result
            return valid
        },
        personGroups (state: State): Group[] {
            return state.personGroups
        },
        personSelectedGroups: (state) => (personId: number): Group[] => {
            //  Get our global list of groups
            const groups = state.personGroups

            //  If for some reason groups do not exist, return empty array
            if (!groups) return []

            //  Find all groups that this person is a member of
            const personGroups = groups.filter(x => x.persons_ids && x.persons_ids.includes(personId))

            //  Return our groups
            return personGroups
        },
        personSelectedInvoices (state: State): Invoice[] {
            return state.personSelectedInvoices
        },
        personSelectedInvoicesCurrentPage (state: State): number {
            return state.personSelectedInvoicesCurrentPage
        },
        personSelectedInvoicesLastPage (state: State): number {
            return state.personSelectedInvoicesLastPage
        },
        personSelectedInvoicesNextPage (state: State): number {
            return state.personSelectedInvoicesNextPage
        },
        personSelectedInvoicesPaginating (state: State): boolean {
            return state.personSelectedInvoicesPaginating
        },
        personSelectedInvoicesParams (state: State): InvoiceParams {
            return state.personSelectedInvoicesParams
        },
        personMediaAttachment (state: State): Media[] {
            //  Grab our list from the global dataset
            const list = state.personSelectedMedia

            //  If no list, return empty array
            if (!list) return []

            //  Else, grab the attachments from this person
            const attachments = list.filter(x => x.folder === 'attachment')

            //  Return our attachments
            return attachments
        },
        personMediaUploaded (state: State): Media[] {
            //  Grab our list from the global dataset
            const list = state.personSelectedMedia

            //  If no list, return empty array
            if (!list) return []

            //  Else, grab the up[loaded from this person
            const uploaded = list.filter(x => x.folder === 'uploaded')

            //  Return our uploaded media
            return uploaded
        },
        personSelectedMedia (state: State): Media[] {
            return state.personSelectedMedia
        },
        personSelectedMediaCurrentPage (state: State): number {
            return state.personSelectedMediaCurrentPage
        },
        personSelectedMediaLastPage (state: State): number {
            return state.personSelectedMediaLastPage
        },
        personSelectedMediaNextPage (state: State): number {
            return state.personSelectedMediaNextPage
        },
        personSelectedMediaPaginating (state: State): boolean {
            return state.personSelectedMediaPaginating
        },
        personSelectedMediaParams (state: State): MediaParams {
            return state.personSelectedMediaParams
        },
        personSelectedMediaParamsDefault (state: State): MediaParams {
            return state.personSelectedMediaParamsDefault
        },
        personProperties (state: State): PersonProperty[] {
            return state.personProperties
        },
        //  Removes support users from a list of person users
        withoutSupport: (_, getters) => (persons: Person[]): Person[] => {
            //  Grab our tenant
            const tenant = getters.tenant

            //  Our filter for removing support users
            const filter = (x: Person) => {
                //  Grab this person's roles
                const roles = x.user.roles

                //  Iterate through this person's roles
                for (let i = 0; i < roles.length; i++) {
                    //  Grab the pivot for this role
                    const pivot = roles[i].pivot

                    //  If the pivot tenant id does not match our current tenant, skip
                    if (pivot.tenant_id !== tenant.id) continue
                    //  Else, if the pivot role id is support (5) then return false to filter
                    if (pivot.role_id === 5) return false
                }

                //  We didn't exit so this is valid, return true
                return true
            }

            //  Run our filter
            const filtered = persons.filter(filter)

            //  Return our filtered persons
            return filtered
        }
    }
}